import { useCallback, useState } from "react";
import type { Oekt } from "./session";

/**
 * Hvilke sider som vises.
 *
 * ========  LISTA DI, IKKE EN TILGANG  ========
 *
 * Samme regel som omraadene: alle kan slaa alt paa og av naar som helst, og
 * ingenting er skjult for noen. Det foelger av den ufravikelige regelen om
 * at alle medlemmer leser alt som ligger i produksjonen.
 *
 * Det dette loeser er noe annet, og brukeren sa det rett ut 27.10.2026:
 * oppsettsflatene er noe man er innom én gang, og saa blar man forbi dem
 * hver dag resten av produksjonen. Da skal man kunne legge dem bort.
 *
 * ========  OMRAADENE SETTER DEN, DE FILTRERER DEN IKKE  ========
 *
 * Fram til 27.10.2026 var omraadene selve filteret. Naa er DENNE lista det
 * eneste som avgjoer hva som vises, og et omraade er en snarvei som skrur
 * sidene sine paa eller av. Ett filter, to maater aa sette det paa — med to
 * filtre ville en bryter staatt paa uten at sida kom fram, og da er det
 * flata som lyver.
 *
 * Omraadene gjoer fortsatt noe lista ikke gjoer: de avgjoer hvilke
 * brieffelt og dokumentbokser som staar framme.
 *
 * ========  PER PRODUKSJON OG PER MEDLEM  ========
 *
 * Det er en vane hos den enkelte, som hurtigknappene. En frilanser paa to
 * produksjoner vil ha ulike lister.
 */

function noekkel(oekt: Oekt): string {
  return `infoflyt.sider.${oekt.production.id}.${oekt.member.id}`;
}

function les(oekt: Oekt, alle: readonly string[]): ReadonlySet<string> {
  try {
    const r = localStorage.getItem(noekkel(oekt));
    if (r === null) return new Set(alle);
    const liste = JSON.parse(r) as unknown;
    if (!Array.isArray(liste)) return new Set(alle);
    /* Bare id-er som FINNES. En side som er fjernet fra koden skal ikke
       ligge igjen i lista og telle som paaslaatt. */
    return new Set(liste.filter((x): x is string =>
      typeof x === "string" && alle.includes(x)));
  } catch {
    return new Set(alle);
  }
}

function skriv(oekt: Oekt, paa: ReadonlySet<string>): void {
  try {
    localStorage.setItem(noekkel(oekt), JSON.stringify([...paa]));
  } catch {
    /* Privat modus kan nekte lagring. Valget gjelder da denne oekta. */
  }
}

export interface Sidevalg {
  readonly paa: ReadonlySet<string>;
  veksle(id: string): void;
  /** Brukes av omraadebryterne. Setter en hel bunke om gangen. */
  settMange(ider: readonly string[], paa: boolean): void;
  /**
   * Lista BLIR disse, og ingenting annet.
   *
   * Omraadebryteren trengte den. `settMange(..., true)` la bare til, og
   * siden lista starter med ALT paa, la den til ingenting — bryteren
   * gjorde da ikke noe synlig i det hele tatt. Et fokusvalg som ikke
   * fjerner noe er ikke et fokusvalg.
   */
  settKun(ider: readonly string[]): void;
  visAlle(): void;
}

export function useSidevalg(oekt: Oekt, alle: readonly string[]): Sidevalg {
  const [paa, setPaa] = useState<ReadonlySet<string>>(() => les(oekt, alle));

  const lagre = useCallback((neste: ReadonlySet<string>): void => {
    setPaa(neste);
    skriv(oekt, neste);
  }, [oekt]);

  const veksle = useCallback((id: string): void => {
    setPaa((forrige) => {
      const neste = new Set(forrige);
      if (neste.has(id)) neste.delete(id); else neste.add(id);
      skriv(oekt, neste);
      return neste;
    });
  }, [oekt]);

  const settMange = useCallback((ider: readonly string[], ja: boolean): void => {
    setPaa((forrige) => {
      const neste = new Set(forrige);
      for (const id of ider) { if (ja) neste.add(id); else neste.delete(id); }
      skriv(oekt, neste);
      return neste;
    });
  }, [oekt]);

  const settKun = useCallback((ider: readonly string[]): void => {
    lagre(new Set(ider.filter((id) => alle.includes(id))));
  }, [lagre, alle]);

  const visAlle = useCallback((): void => { lagre(new Set(alle)); }, [lagre, alle]);

  return { paa, veksle, settMange, settKun, visAlle };
}
